import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * The small "type" badge that leads each classification row in the advanced
 * panels — Sector / Industry Group / Industry / Region / Country / Exchange.
 *
 * It carries the live-status signal that used to be a separate left-margin dot:
 * tinted light GREEN when the item holds constituents in the selected universe
 * today, light RED when it does not. Every row stays selectable regardless — a
 * point-in-time backtest may legitimately exclude a code the universe has since
 * dropped (a retired industry, Russia, a delisted venue) — so the colour
 * informs without gating. `title` carries the meaning for non-colour users.
 */
export function ClassificationTypeBadge({
  label,
  isLive,
}: {
  label: string;
  isLive: boolean;
}) {
  return (
    <Badge
      variant="outline"
      title={
        isLive
          ? 'Holds constituents in the current universe'
          : 'Not in the current universe: still selectable for a point-in-time backtest'
      }
      className={cn(
        // A card-opaque layer sits UNDER the translucent tint (before:bg-card)
        // so an excluded row's red wash (bg-destructive/[0.08]) can't bleed
        // through and muddy the green/grey — the badge stays legible while the
        // rest of the row still reads as excluded. `isolate` keeps the -z-10
        // backing inside the badge, not behind the row.
        'relative isolate text-xs shrink-0 before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-card',
        // Live = green, NOT live = neutral grey (never red: red is reserved for
        // exclusion, so a red "not in the universe today" badge read as
        // "excluded", which it is not).
        //
        // Same ramps as the R&D-hub status pills (getStatusColor in
        // shared/utils/stringUtils) so a green here reads like the Live-Model
        // badge and a grey like a backtest card — one visual language for
        // live-vs-inactive across the app. The green carries a slightly stronger
        // tint + border than the status pill because this badge has no coloured
        // dot next to it to draw the eye.
        isLive
          ? 'bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400'
          : 'bg-slate-500/10 text-slate-700 border-slate-500/20 dark:text-slate-300',
      )}
    >
      {label}
    </Badge>
  );
}
